import styles from "./MobileSearchLocationSlide.module.css";
import {
    TargetLocationIcon as LocationSearchIcon,
    MagnifyingGlassIcon as SearchIcon,
    NavigationIcon as UseLocationPointerIcon,
} from "@wags/ui/product-icons.tsx";

/* eslint-disable react-doctor/no-chain-state-updates, react-doctor/no-event-handler, react-doctor/prefer-tag-over-role, react-hooks-js/set-state-in-effect */
import { Button } from "@wags/ui/button.tsx";
import { cn } from "@wags/ui/ui-utils";
import { type ReactNode, useId, useRef, useState } from "react";
import {
    type LocationSelection,
    type SuggestedLocation,
    useLocationAutocomplete,
} from "@/features/viewer-location";
import { useLocationRequest } from "./use-location-request";
import { useComboboxNavigation } from "../shared/useComboboxNavigation";
import { OPTION_ROW_CLASS, OptionGroup } from "../shared/OptionGroup";

type MobileSearchLocationSlideProps = {
    defaultValue?: string;
    lat?: number;
    lng?: number;
    onLocationChange?: (location: LocationSelection) => void;

    autoFocus?: boolean;

    quietSlot?: ReactNode;

    onClose?: () => void;
    onSelectionReadyChange?: (ready: boolean) => void;
};

function LocationSuggestions({
    hasSelected,
    trimmedInput,
    suggestions,
    combobox,
    onSelect,
    loading,
    resolving,
}: {
    hasSelected: boolean;
    trimmedInput: string;
    suggestions: SuggestedLocation[];
    combobox: ReturnType<typeof useComboboxNavigation>;
    onSelect: (suggestion: SuggestedLocation) => void;
    loading: boolean;
    resolving: boolean;
}) {
    if (hasSelected || trimmedInput.length < 3) return null;
    if (suggestions.length === 0)
        return loading ? null : (
            <p className="mt-6 text-body-small text-doberman-black/45">
                No matching locations found.
            </p>
        );
    return (
        <OptionGroup
            {...combobox.listboxProps}
            role="listbox"
            aria-label="Location suggestions"
            className="mt-6 gap-0"
        >
            {suggestions.map((suggestion, index) => {
                const hasPrefix = suggestion.label
                    .toLowerCase()
                    .startsWith(trimmedInput.toLowerCase());
                const prefix = hasPrefix
                    ? suggestion.label.slice(0, trimmedInput.length)
                    : "";
                const suffix = hasPrefix
                    ? suggestion.label.slice(trimmedInput.length)
                    : suggestion.label;
                return (
                    <button
                        key={suggestion.id}
                        {...combobox.getOptionProps(index)}
                        role="option"
                        type="button"
                        aria-selected={index === combobox.highlightedIndex}
                        onMouseDown={(event) => {
                            event.preventDefault();
                        }}
                        onClick={() => onSelect(suggestion)}
                        disabled={resolving}
                        className={cn(
                            OPTION_ROW_CLASS,
                            "gap-3 py-2.5 font-family-body text-base disabled:cursor-wait",
                            index === combobox.highlightedIndex &&
                                "bg-doberman-black/4",
                        )}
                    >
                        <span className={styles.suggestionIcon}>
                            <LocationSearchIcon
                                className="size-4"
                                aria-hidden="true"
                            />
                        </span>
                        <span className="text-black">
                            {prefix}
                            {suffix ? (
                                <span className="text-black/50">{suffix}</span>
                            ) : null}
                        </span>
                    </button>
                );
            })}
        </OptionGroup>
    );
}

function getQuietSlot(
    quietSlot: ReactNode,
    trimmedInput: string,
    hasSelected: boolean,
) {
    if (!quietSlot) return null;
    return trimmedInput.length === 0 || hasSelected ? quietSlot : null;
}

function locationListboxOpen(
    selected: boolean,
    loading: boolean,
    input: string,
    suggestions: SuggestedLocation[],
) {
    return !selected && !loading && input.length >= 3 && suggestions.length > 0;
}

function LocationInputAction({
    loading,
    hasValue,
    onClear,
}: {
    loading: boolean;
    hasValue: boolean;
    onClear: () => void;
}) {
    return loading ? (
        <div
            role="status"
            className="-mr-2 flex min-h-11 min-w-11 shrink-0 items-center justify-center"
        >
            <span
                aria-hidden="true"
                className="size-4 animate-spin rounded-full border-2 border-true-black/30 border-t-transparent motion-reduce:animate-none"
            />
            <span className="sr-only">Finding location</span>
        </div>
    ) : hasValue ? (
        <button
            type="button"
            onClick={onClear}
            aria-label="Clear location"
            className="flex shrink-0 items-center justify-center min-h-11 min-w-11 -mr-2"
        >
            <span className="flex items-center justify-center size-5 rounded-full bg-true-black/8 text-doberman-black/40 active:bg-true-black/12 transition-colors">
                <span className="text-caption leading-none">×</span>
            </span>
        </button>
    ) : null;
}

function LocationInputError({
    request,
    id,
}: {
    request: ReturnType<typeof useLocationRequest>;
    id: string;
}) {
    if (request.state.status !== "error") return null;
    const retry = request.state.retry;
    return (
        <div className="mt-1 flex min-h-11 items-center gap-1 px-1 text-body-small leading-5">
            <p id={id} role="alert" className="min-w-0 text-destructive">
                Couldn’t confirm location.
            </p>
            <button
                type="button"
                onClick={() => {
                    void request.select(retry);
                }}
                className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-nested px-2 font-medium text-doberman-black underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow"
            >
                Retry
            </button>
        </div>
    );
}

export function MobileSearchLocationSlide({
    defaultValue = "",
    lat,
    lng,
    onLocationChange,
    autoFocus = true,
    quietSlot,
    onClose,
    onSelectionReadyChange,
}: MobileSearchLocationSlideProps) {
    const [localValue, setLocalValue] = useState(defaultValue);
    const [lastDefaultValue, setLastDefaultValue] = useState(defaultValue);
    const [hasUserInput, setHasUserInput] = useState(false);

    const [hasSelected, setHasSelected] = useState(false);
    const showCancel = Boolean(onClose);
    const inputRef = useRef<HTMLInputElement>(null);
    const feedbackId = useId();
    const trimmedInput = localValue.trim();
    const locationRequest = useLocationRequest({
        onReadyChange: onSelectionReadyChange,
        onSelected: (location) => {
            setHasUserInput(true);
            setHasSelected(true);
            setLocalValue(location.label);
            inputRef.current?.blur();
            onLocationChange?.(location);
        },
    });
    const resolving = locationRequest.state.status === "pending";
    const locationFailed = locationRequest.state.status === "error";

    const setInputElement = (element: HTMLInputElement | null) => {
        inputRef.current = element;
        if (!element || !autoFocus) return;
        const timer = setTimeout(() => element.focus(), 400);
        return () => clearTimeout(timer);
    };

    const {
        autocompleteSuggestions: locationSuggestions,
        isLoading,
        getLocationFromSuggestion,
        getCurrentLocationSelection,
    } = useLocationAutocomplete({
        inputValue: localValue,
        isOpen: true,
        lat,
        lng,
        prefetchInput: localValue,
        includeNearbySuggestions: false,
        shouldDebounce: hasUserInput,
    });

    if (defaultValue !== lastDefaultValue) {
        setLastDefaultValue(defaultValue);
        if (!hasUserInput) {
            setLocalValue(defaultValue);
        }
    }

    const isListboxOpen = locationListboxOpen(
        hasSelected,
        resolving || isLoading,
        trimmedInput,
        locationSuggestions,
    );

    const combobox = useComboboxNavigation({
        itemCount: resolving || isLoading ? 0 : locationSuggestions.length,
        isOpen: isListboxOpen,
        onSelect: (index) => {
            void handleSuggestionSelect(locationSuggestions[index]);
        },
    });

    const handleSuggestionSelect = (suggestion: SuggestedLocation) =>
        locationRequest.select(() => getLocationFromSuggestion(suggestion));

    return (
        <>
            <div className={cn(showCancel && "flex items-center gap-2")}>
                <div
                    className={cn(
                        "flex items-center gap-2.5 rounded-nested border-shadow h-12 bg-true-white px-4",
                        showCancel && "min-w-0 flex-1",
                        locationFailed &&
                            "outline outline-1 outline-destructive/40",
                    )}
                >
                    <span className={styles.searchIcon}>
                        <SearchIcon className="size-5" />
                    </span>
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <input
                            ref={setInputElement}
                            value={localValue}
                            {...combobox.inputProps}
                            onKeyDown={combobox.handleKeyDown}
                            aria-label="City, state, or zip code"
                            aria-busy={resolving}
                            aria-invalid={locationFailed || undefined}
                            aria-describedby={
                                locationFailed ? feedbackId : undefined
                            }
                            onChange={(event) => {
                                locationRequest.cancel();
                                setHasUserInput(true);
                                setHasSelected(false);
                                setLocalValue(event.target.value);
                                combobox.resetHighlight();
                            }}
                            placeholder="City, state, or zip code"
                            className="block w-full min-w-0 flex-1 bg-transparent text-base leading-6 text-doberman-black outline-none placeholder:text-doberman-black/45"
                        />
                    </div>
                    <LocationInputAction
                        loading={isLoading || resolving}
                        hasValue={trimmedInput.length > 0}
                        onClear={() => {
                            locationRequest.cancel();
                            setHasSelected(false);
                            setLocalValue("");
                            setHasUserInput(true);
                            inputRef.current?.focus();
                        }}
                    />
                </div>
                {showCancel && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-12 shrink-0 items-center justify-center rounded-nested px-1 text-body-small text-doberman-black/70 transition-colors [@media(hover:hover)]:hover:text-doberman-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow"
                    >
                        Cancel
                    </button>
                )}
            </div>

            <LocationInputError request={locationRequest} id={feedbackId} />

            <Button
                variant="muted"
                onClick={() => {
                    void locationRequest.select(getCurrentLocationSelection);
                }}
                disabled={resolving}
                className="mt-1 h-11 w-full"
            >
                <UseLocationPointerIcon className="size-4" />
                Use my current location
            </Button>

            {!resolving && getQuietSlot(quietSlot, trimmedInput, hasSelected)}

            {trimmedInput.length > 0 && trimmedInput.length < 3 ? (
                <p className="mt-4 text-body-small text-doberman-black/45">
                    Keep typing to see suggestions…
                </p>
            ) : null}

            <LocationSuggestions
                hasSelected={hasSelected}
                loading={isLoading}
                resolving={resolving}
                trimmedInput={trimmedInput}
                suggestions={locationSuggestions}
                combobox={combobox}
                onSelect={(suggestion) => {
                    void handleSuggestionSelect(suggestion);
                }}
            />
        </>
    );
}
